<template>
  <div :id="profileContainerId" ref="canvasRoot" class="silo-profile-canvas-root">
    <canvas :id="profileCanvasId"></canvas>
  </div>
</template>

<script>
import {
  fillGeometryFromLevelPercent,
  parseSiloDimensionsFromCurve,
} from '../utils/siloLevelToKg';

function seriesToCurvePoints(series) {
  return {
    x: series.map((point) => point.x),
    y: series.map((point) => point.y),
  };
}

function intersectAtY(start, end, y) {
  const dy = end.y - start.y;
  if (Math.abs(dy) < 1e-12) return { x: end.x, y };
  const t = (y - start.y) / dy;
  return {
    x: start.x + t * (end.x - start.x),
    y,
  };
}

function clipPolygonBelowY(points, fillY) {
  if (!Array.isArray(points) || points.length < 2) return [];

  const output = [];
  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const previous = points[(i + points.length - 1) % points.length];
    const currentInside = current.y <= fillY + 1e-9;
    const previousInside = previous.y <= fillY + 1e-9;

    if (currentInside) {
      if (!previousInside) {
        output.push(intersectAtY(previous, current, fillY));
      }
      output.push(current);
    } else if (previousInside) {
      output.push(intersectAtY(previous, current, fillY));
    }
  }

  return output;
}

const STRAIGHT_FILL_WAVES = 1;
const STRAIGHT_FILL_WAVE_SAMPLES = 28;

function buildWavePoints(xStart, xEnd, fillY, amplitude) {
  const points = [];
  for (let i = 0; i <= STRAIGHT_FILL_WAVE_SAMPLES; i += 1) {
    const t = i / STRAIGHT_FILL_WAVE_SAMPLES;
    points.push({
      x: xStart + (xEnd - xStart) * t,
      y: fillY + amplitude * Math.sin(t * STRAIGHT_FILL_WAVES * Math.PI * 2),
    });
  }
  return points;
}

function replaceFlatTopWithWave(clipped, fillY, amplitude) {
  if (!clipped.length) return clipped;

  let topEdge = -1;
  for (let i = 0; i < clipped.length; i += 1) {
    const current = clipped[i];
    const next = clipped[(i + 1) % clipped.length];
    if (Math.abs(current.y - fillY) <= 1e-6 && Math.abs(next.y - fillY) <= 1e-6) {
      topEdge = i;
      break;
    }
  }

  if (topEdge < 0) return clipped;

  const start = clipped[topEdge];
  const end = clipped[(topEdge + 1) % clipped.length];
  const wave = buildWavePoints(start.x, end.x, fillY, amplitude);
  return [
    ...clipped.slice(0, topEdge + 1),
    ...wave.slice(1, -1),
    ...clipped.slice(topEdge + 1),
  ];
}

function buildStraightGrainPolygon(siloPoints, fillY, yMin, yMax) {
  const clipped = clipPolygonBelowY(siloPoints, fillY);
  if (clipped.length < 3) return clipped;

  const height = yMax - yMin;
  const roomUp = Math.max(0, yMax - fillY);
  const roomDown = Math.max(0, fillY - yMin);
  const amplitude = Math.min(height * 0.03, 0.35, roomUp * 0.65, roomDown * 0.22);
  if (amplitude < height * 0.004) return clipped;

  return replaceFlatTopWithWave(clipped, fillY, amplitude);
}

function resolveFillY(siloPoints, level) {
  const yMin = Math.min(...siloPoints.map((point) => point.y));
  const yMax = Math.max(...siloPoints.map((point) => point.y));
  const dims = parseSiloDimensionsFromCurve(seriesToCurvePoints(siloPoints));
  const geometry = fillGeometryFromLevelPercent(level, dims);
  if (geometry?.fillHeightM > 0) {
    return yMin + geometry.fillHeightM;
  }

  const ratio = Math.min(1, Math.max(0, Number(level) || 0) / 100);
  return yMin + ratio * (yMax - yMin);
}

export default {
  name: "SiloProfileVisualization",
  props: {
    siloSeries: {
      type: Array,
      default: () => [],
    },
    profileSeries: {
      type: Array,
      default: () => [],
    },
    fillGrainArea: {
      type: Boolean,
      default: false,
    },
    verifiedProfileSeries: {
      type: Array,
      default: () => [],
    },
    straightFill: {
      type: Boolean,
      default: false,
    },
    level: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      profileContainerId: '',
      profileCanvasId: '',
      containerWidth: 0,
      containerHeight: 0,
      resizeObserver: null,
      resizeHandler: null,
    };
  },
  methods: {
    measureContainer() {
      const root = this.$refs.canvasRoot;
      if (!root) return false;

      const rect = root.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);

      if (width <= 0 || height <= 0) return false;

      this.containerWidth = width;
      this.containerHeight = height;
      return true;
    },
    setupResizeObserver() {
      const root = this.$refs.canvasRoot;
      if (!root) return;

      const updateSize = () => {
        if (this.measureContainer()) {
          this.renderCanvas();
        }
      };

      updateSize();

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(updateSize);
        this.resizeObserver.observe(root);
      } else {
        this.resizeHandler = updateSize;
        window.addEventListener('resize', this.resizeHandler);
      }
    },
    resolveGrainPoints(siloPoints, grainsPoints) {
      if (!this.straightFill) {
        return grainsPoints || [];
      }

      const fillY = resolveFillY(siloPoints, this.level);
      const yMin = Math.min(...siloPoints.map((point) => point.y));
      const yMax = Math.max(...siloPoints.map((point) => point.y));
      if (fillY <= yMin) return [];
      if (fillY >= yMax) return siloPoints;
      return buildStraightGrainPolygon(siloPoints, fillY, yMin, yMax);
    },
    renderCanvas() {
      const siloCanvas = document.getElementById(this.profileCanvasId);
      const hasGrainSeries = this.straightFill
        || (this.profileSeries && this.profileSeries.length > 0)
        || (this.verifiedProfileSeries && this.verifiedProfileSeries.length > 0);

      if (!siloCanvas || !this.siloSeries?.length || !hasGrainSeries) {
        return;
      }

      if (!this.measureContainer()) {
        return;
      }

      const width = this.containerWidth;
      const height = this.containerHeight;
      const dpr = window.devicePixelRatio || 1;

      siloCanvas.width = Math.floor(width * dpr);
      siloCanvas.height = Math.floor(height * dpr);
      siloCanvas.style.width = `${width}px`;
      siloCanvas.style.height = `${height}px`;
      siloCanvas.style.maxWidth = 'none';
      siloCanvas.style.maxHeight = 'none';

      const ctx = siloCanvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const grainSeries = this.verifiedProfileSeries?.length
        ? this.verifiedProfileSeries
        : this.profileSeries;
      this.drawPolygon(ctx, this.siloSeries, grainSeries, width, height);
    },
    drawPolygon(ctx, siloPoints, grainsPoints, canvasWidth, canvasHeight) {
      const topPadding = Math.max(14, canvasHeight * 0.06);
      const bottomPadding = Math.max(10, canvasHeight * 0.05);
      const rightPadding = Math.max(8, canvasWidth * 0.04);
      const fontSize = Math.max(10, Math.min(13, canvasHeight * 0.034));
      const distanceFromSilo = 10;
      const labelGap = 5;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      const xMin = Math.min(...siloPoints.map((point) => point.x));
      const xMax = Math.max(...siloPoints.map((point) => point.x));
      const yMin = Math.min(...siloPoints.map((point) => point.y));
      const yMax = Math.max(...siloPoints.map((point) => point.y));

      const xRange = (xMax - xMin) || 1;
      const yRange = (yMax - yMin) || 1;
      const totalHeight = yRange;

      let markInterval;

      if (totalHeight <= 10) {
        markInterval = 2;
      } else if (totalHeight <= 20) {
        markInterval = 4;
      } else if (totalHeight <= 30) {
        markInterval = 6;
      } else {
        markInterval = 8;
      }

      const tolerance = totalHeight <= 10 ? 1 :
                        totalHeight <= 20 ? 2 :
                        totalHeight <= 30 ? 3 : 4;

      const formatMark = (meters, isTop = false) => {
        if (isTop) {
          const rounded2 = Math.round(meters * 100) / 100;
          const rounded1 = Math.round(meters * 10) / 10;
          if (Number.isInteger(rounded2)) return `${rounded2} m`;
          if (Math.abs(rounded2 - rounded1) < 0.001) return `${rounded1.toFixed(1)} m`;
          return `${rounded2.toFixed(2)} m`;
        }
        if (Number.isInteger(meters)) return `${meters} m`;
        return `${meters.toFixed(1)} m`;
      };

      ctx.font = `${fontSize}px Arial, sans-serif`;
      const labelStrings = [];
      for (let meters = yMin; meters <= yMax; meters += markInterval) {
        if (Math.abs(yMax - meters) <= tolerance && meters !== yMax) continue;
        labelStrings.push(formatMark(meters));
      }
      labelStrings.push(formatMark(yMax, true));

      const maxLabelWidth = Math.max(
        ...labelStrings.map((label) => ctx.measureText(label).width),
        0
      );

      let labelMargin = Math.ceil(maxLabelWidth + 18 + labelGap + 12);
      labelMargin = Math.max(48, Math.min(labelMargin, canvasWidth * 0.42));

      let widthValue = Math.max(80, canvasWidth - labelMargin - rightPadding);
      let tickLong = Math.max(18, widthValue * 0.08);
      labelMargin = Math.ceil(maxLabelWidth + tickLong + labelGap + 12);
      labelMargin = Math.max(48, Math.min(labelMargin, canvasWidth * 0.42));
      widthValue = Math.max(80, canvasWidth - labelMargin - rightPadding);
      tickLong = Math.max(18, widthValue * 0.08);
      const tickShort = Math.max(8, tickLong * 0.45);

      const heightValue = Math.max(80, canvasHeight - topPadding - bottomPadding);
      const adjustX = labelMargin;
      const adjustY = topPadding;
      const xScale = widthValue / xRange;
      const yScale = heightValue / yRange;
      const scale = Math.min(xScale, yScale);

      const drawWidth = xRange * scale;
      const drawHeight = yRange * scale;
      const offsetX = adjustX + (widthValue - drawWidth) / 2;
      const offsetY = adjustY + (heightValue - drawHeight) / 2;

      const mapPoint = (point) => ({
        x: offsetX + (point.x - xMin) * scale,
        y: offsetY + drawHeight - (point.y - yMin) * scale,
      });

      const grainSourcePoints = this.resolveGrainPoints(siloPoints, grainsPoints);
      const scaledSiloPoints = siloPoints.map(mapPoint);
      const scaledGrainsPoints = grainSourcePoints.map(mapPoint);

      const minSiloX = Math.min(...scaledSiloPoints.map((point) => point.x));
      const baseLineX = minSiloX - distanceFromSilo;
      const axisBottomY = offsetY + drawHeight;
      const axisTopY = offsetY;
      const labelX = Math.max(
        maxLabelWidth + 4,
        baseLineX - tickLong - labelGap
      );

      ctx.beginPath();
      ctx.moveTo(baseLineX, axisBottomY);
      ctx.lineTo(baseLineX, axisTopY);
      ctx.strokeStyle = "#404040";
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(scaledSiloPoints[0].x, scaledSiloPoints[0].y);

      for (let i = 1; i < scaledSiloPoints.length; i++) {
        ctx.lineTo(scaledSiloPoints[i].x, scaledSiloPoints[i].y);
      }

      ctx.closePath();

      if (scaledGrainsPoints.length > 1) {
        if (this.straightFill) {
          ctx.save();
          ctx.clip();
        }

        ctx.beginPath();
        ctx.moveTo(scaledGrainsPoints[0].x, scaledGrainsPoints[0].y);

        for (let i = 1; i < scaledGrainsPoints.length; i++) {
          ctx.lineTo(scaledGrainsPoints[i].x, scaledGrainsPoints[i].y);
        }

        ctx.closePath();

        if (this.fillGrainArea) {
          ctx.fillStyle = "#71c90dbc";
          ctx.fill();
        }

        if (!this.straightFill) {
          ctx.strokeStyle = "#71c90dbc";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(scaledGrainsPoints[0].x, scaledGrainsPoints[0].y);
          for (let i = 1; i < scaledGrainsPoints.length; i++) {
            ctx.lineTo(scaledGrainsPoints[i].x, scaledGrainsPoints[i].y);
          }
          ctx.stroke();
        }

        if (this.straightFill) {
          ctx.restore();
        }
      }

      ctx.strokeStyle = "#404040";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(scaledSiloPoints[0].x, scaledSiloPoints[0].y);
      for (let i = 1; i < scaledSiloPoints.length; i++) {
        ctx.lineTo(scaledSiloPoints[i].x, scaledSiloPoints[i].y);
      }
      ctx.stroke();

      ctx.fillStyle = "#000000";
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      const markY = (meters) => axisBottomY - ((meters - yMin) / totalHeight) * drawHeight;

      for (let meters = yMin; meters <= yMax; meters += markInterval) {
        if (Math.abs(yMax - meters) <= tolerance && meters !== yMax) {
          continue;
        }

        const labelY = markY(meters);

        ctx.fillText(formatMark(meters), labelX, labelY);
        ctx.beginPath();
        ctx.moveTo(baseLineX - tickLong, labelY);
        ctx.lineTo(baseLineX, labelY);
        ctx.strokeStyle = "#404040";
        ctx.lineWidth = 1;
        ctx.stroke();

        if (meters < yMax) {
          const midMeters = meters + (markInterval / 2);
          if (midMeters < yMax) {
            const midY = markY(midMeters);
            ctx.beginPath();
            ctx.moveTo(baseLineX - tickShort, midY);
            ctx.lineTo(baseLineX, midY);
            ctx.strokeStyle = "#404040";
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      ctx.fillText(formatMark(yMax, true), labelX, markY(yMax));
      ctx.beginPath();
      ctx.moveTo(baseLineX - tickLong, markY(yMax));
      ctx.lineTo(baseLineX, markY(yMax));
      ctx.strokeStyle = "#404040";
      ctx.lineWidth = 1;
      ctx.stroke();
    },
  },
  watch: {
    siloSeries: {
      handler() {
        this.$nextTick(() => this.renderCanvas());
      },
      deep: true,
    },
    profileSeries: {
      handler() {
        this.$nextTick(() => this.renderCanvas());
      },
      deep: true,
    },
    verifiedProfileSeries: {
      handler() {
        this.$nextTick(() => this.renderCanvas());
      },
      deep: true,
    },
    fillGrainArea() {
      this.$nextTick(() => this.renderCanvas());
    },
    straightFill() {
      this.$nextTick(() => this.renderCanvas());
    },
    level() {
      this.$nextTick(() => this.renderCanvas());
    },
  },
  beforeMount() {
    this.profileContainerId = `profile-viz-canvas-container-${Math.random()}`;
    this.profileCanvasId = `silo-profile-viz-chart-${Math.random()}`;
  },
  mounted() {
    this.$nextTick(() => {
      this.setupResizeObserver();
    });
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  },
};
</script>

<style scoped>
.silo-profile-canvas-root {
  width: 100%;
  height: 100%;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.silo-profile-canvas-root canvas {
  display: block;
  flex-shrink: 0;
}
</style>
